import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import ReactTooltip from 'react-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import { Button } from '@mui/material';

import ErrorHandler from '../components/ErrorHandler';
import { TOAST_TYPE_MAP, ToastCustom } from '../components/ToastCustom';
import { removeUnderscoreToCapitalize } from '../helpers/FormattingHelpers';
import { getUserSettings } from '../helpers/UserHelpers';
import { useCreateBox } from '../services/hooks/useCreateBox';
import { useCreateScan } from '../services/hooks/useCreateScan';
import { useGetCustomers } from '../services/hooks/useGetCustomers';
import { useUpdateScan } from '../services/hooks/useUpdateScan';
import { useAllScanTypes } from '../services/scan-types/queries';
import { GroupType, ScanType } from '../services/scan-types/schemas';
import { PatchScanPayload, ScanRes } from '../types/Scan';
import { User } from '../types/User';
import { UserSettings } from '../types/UserSettings';

import '../styles/views/ScanCreateEditModal.less';

interface Props {
    destinations: Array<string>;
    currentUser: User;
    scanToEdit?: ScanRes;
    closeMe: () => void;
}

const ScanCreateEditModal = ({ currentUser, scanToEdit, destinations, closeMe }: Props): ReactElement => {
    const navigate = useNavigate();
    const inEditMode = Boolean(scanToEdit);
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { isDirty },
        control
    } = useForm();

    const allScanTypesParams = {
        params: {
            page: 1,
            show: 100
        }
    };

    const {
        data: scanTypesResponse,
        isLoading: isLoadingTypes,
        error: scanTypesError
    } = useAllScanTypes(allScanTypesParams);

    const { mutate: createScanMutation, isPending: isCreateScanPending } = useCreateScan(
        // onSuccess callback
        (data) => {
            if (data.isBoxScan) {
                // We create one empty box when creating Box Scan
                createBoxMutation({ scanId: data.id });
            } else {
                // Regular scan so no box needed, we navigate immediately
                navigate(`/scanning/${data.id}`);
            }
        },
        // onError callback
        () => closeMe()
    );

    const { mutate: createBoxMutation, isPending: isPendingCreateBox } = useCreateBox({
        onSuccess: (boxData) => {
            navigate(`/scanning/${boxData.scanId}`); // We navigate to the scan once the box is created
        }
    });

    const {
        mutate: updateScanMutation,
        isSuccess: updateScanMutationSuccess,
        error: updateScanMutationErr,
        isPending: updateScanMutationPending
    } = useUpdateScan();

    const customersQueryResult = useGetCustomers();

    const [userSettings] = useState<UserSettings | null>(getUserSettings());
    const isAllowedToAssignScanToCustomer = userSettings?.allowedToAssignLoggers;

    const isCreatingOrUpdating = isCreateScanPending || updateScanMutationPending || isPendingCreateBox;

    const scanType = watch('scanType');

    useEffect(() => {
        if (scanToEdit) {
            setValue('trackingId', scanToEdit.trackingId);
            setValue('orderId', scanToEdit.salesOrderId);
        }
    }, [scanToEdit]);

    useEffect(() => {
        if (scanTypesError) {
            ErrorHandler(scanTypesError);
        }
    }, [scanTypesError]);

    const destinationOptions = useMemo(() => {
        if (!destinations || !currentUser) return [];

        return [
            { value: '', label: '- Please Select -' },
            ...destinations
                .filter((destination) => destination !== currentUser.locationName)
                .map((destination) => ({
                    value: destination,
                    label: destination
                }))
        ];
    }, [destinations, currentUser]);

    useEffect(() => {
        if (customersQueryResult.error) {
            ErrorHandler(customersQueryResult.error, 'Error fetching customers');
        }
    }, [customersQueryResult.error]);

    const customerOptions = useMemo(() => {
        if (!customersQueryResult.data) return [];

        return [
            { value: '', label: '- Please Select -' },
            ...customersQueryResult.data
                .slice()
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((elem) => ({ value: elem.id, label: elem.name }))
        ];
    }, [customersQueryResult.data]);

    useEffect(() => {
        if (scanToEdit) {
            const showDestination = scanToEdit.scanTypeGroupType === GroupType.OUTBOUND;

            setValue('scanType', {
                value: scanToEdit.scanTypeName,
                label: removeUnderscoreToCapitalize(scanToEdit.scanTypeName)
            });

            if (showDestination && inEditMode) {
                setValue(
                    'destination',
                    scanToEdit.destinationName
                        ? {
                              value: scanToEdit.destinationName,
                              label: scanToEdit.destinationName
                          }
                        : undefined
                );
            }
            if (customerOptions.length > 0) {
                const assignedCustomerOption = customerOptions.find(
                    (elem) => Number(elem.value) === scanToEdit.assignedCustomerId
                );
                setValue('customer', assignedCustomerOption ?? null);
            }
        }
    }, [scanToEdit, customerOptions]);

    useEffect(() => {
        if (updateScanMutationSuccess) {
            ToastCustom('Scan successfully saved', TOAST_TYPE_MAP.SUCCESS);
            closeMe();
        } else if (updateScanMutationErr) {
            ErrorHandler(updateScanMutationErr);
            closeMe();
        }
    }, [updateScanMutationSuccess || updateScanMutationErr]);

    const submitScan = (data: any) => {
        const setCustomerInfo = !scanToEdit || scanToEdit.statusName !== 'COMPLETE';

        const payload: PatchScanPayload = {
            trackingId: data.trackingId,
            salesOrderId: data.orderId,
            ...(!inEditMode && { scanTypeId: data.scanType.scanTypeId }),
            ...(setCustomerInfo && data.customer && { assignedCustomerId: data.customer.value }),
            ...(data.destination && { destinationName: data.destination.value })
        };

        if (inEditMode && scanToEdit) {
            updateScanMutation({ scanId: scanToEdit.id, payload: payload });
        } else {
            createScanMutation(payload);
        }
    };

    const getScanTypes = () => {
        if (scanTypesResponse) {
            return scanTypesResponse.items
                .map((scanType) => ({
                    value: scanType.name,
                    label: scanType.name,
                    scanTypeId: scanType.id
                }))
                .sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
        }
        return [];
    };

    const isScanOutboundGroup = isScanOfGroupTypeOutbound(
        scanToEdit?.scanTypeName ?? scanType?.value,
        scanTypesResponse?.items
    );

    return (
        <Modal
            isOpen={true}
            ariaHideApp={false}
            className="modal create-edit-scan-modal"
            overlayClassName="modal-overlay"
            shouldCloseOnOverlayClick={false}
        >
            <div className="modal-header">
                {inEditMode ? 'Edit' : 'New'} Scan
                <FontAwesomeIcon
                    onClick={() => closeMe()}
                    className="close-icon"
                    icon={['far', 'times']}
                    id="dimgray"
                />
            </div>

            <div className="info-text">
                <FontAwesomeIcon icon="info-circle" className="margin-right" />
                <span>
                    Fields in <strong>bold</strong> are required!
                </span>
            </div>

            <form className="new-scan-form" onSubmit={handleSubmit(submitScan)}>
                <div className="form-elem select-container" data-testid="scan-type-dropdown">
                    <label htmlFor="scan-type">Scan Type</label>
                    <Controller
                        control={control}
                        name="scanType"
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                aria-label="scan-type-search"
                                styles={searchSelectStyles}
                                maxMenuHeight={230}
                                isDisabled={Boolean(scanToEdit)}
                                isLoading={isLoadingTypes}
                                placeholder="- Please Select -"
                                options={getScanTypes()}
                            />
                        )}
                    />
                </div>

                {isScanOutboundGroup && (
                    <>
                        <div className="form-elem select-container">
                            <ReactTooltip type="dark" effect="solid" id="customer-tip" place="bottom" delayShow={200} />
                            <label className="reg-label" htmlFor="customer">
                                Customer
                            </label>
                            <div
                                data-for="customer-tip"
                                data-testid="customer-search"
                                {...(!isAllowedToAssignScanToCustomer && {
                                    'data-tip': 'You are not allowed to assign a customer to this scan'
                                })}
                            >
                                <Controller
                                    control={control}
                                    defaultValue={null}
                                    name="customer"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            styles={searchSelectStyles}
                                            maxMenuHeight={230}
                                            aria-label="customer-search"
                                            isDisabled={
                                                !isAllowedToAssignScanToCustomer ||
                                                (scanToEdit && scanToEdit?.statusName === 'COMPLETE')
                                            }
                                            isLoading={customersQueryResult.isLoading}
                                            placeholder="- Please Select -"
                                            options={customerOptions}
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div className="form-elem select-container">
                            <ReactTooltip
                                type="dark"
                                effect="solid"
                                id="destinations-tip"
                                place="bottom"
                                delayShow={200}
                            />
                            <label className="reg-label" htmlFor="destination">
                                Destination
                            </label>
                            <div
                                data-testid="destination-dropdown"
                                data-for="destinations-tip"
                                data-tip={getDestinationTooltipText(scanType, isScanOutboundGroup)}
                            >
                                <Controller
                                    control={control}
                                    defaultValue=""
                                    name="destination"
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            aria-label="destination-search"
                                            styles={searchSelectStyles}
                                            maxMenuHeight={230}
                                            isDisabled={!isScanOutboundGroup}
                                            placeholder="- Please Select -"
                                            options={destinationOptions}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </>
                )}

                <div className="form-elem">
                    <label className="reg-label" htmlFor="tracking-id">
                        {scanType?.value.startsWith('MANUFACTURER') ? 'Batch Number' : 'Tracking ID'}
                    </label>
                    <input {...register('trackingId')} id="tracking-id" />
                </div>

                <div className="form-elem">
                    <label className="reg-label" htmlFor="order-id">
                        {scanType?.value.startsWith('MANUFACTURER') ? 'Shipment Number' : 'Sales order ID'}
                    </label>
                    <input {...register('orderId')} id="order-id" />
                </div>

                <div className="button-container">
                    <Button variant="outlined" onClick={closeMe}>
                        Cancel
                    </Button>

                    <LoadingButton
                        variant="contained"
                        type="submit"
                        loading={isCreatingOrUpdating}
                        disabled={isCreatingOrUpdating || !scanType || !isDirty}
                    >
                        {inEditMode ? 'Save' : 'Create'} Scan
                    </LoadingButton>
                </div>
            </form>
        </Modal>
    );
};

export const searchSelectStyles = {
    input: (base: any): any => ({
        ...base,
        '[type="text"]': {
            boxShadow: 'none'
        }
    }),
    control: (provided: any): any => ({
        ...provided,
        boxShadow: 'none',
        width: '100%',
        height: 40
    })
};

const isScanOfGroupTypeOutbound = (scanType: string | undefined, scanTypes: Array<ScanType> | undefined) => {
    return (scanTypes ?? []).some((type) => type.groupType === GroupType.OUTBOUND && scanType === type.name);
};

const getDestinationTooltipText = (scanType: { value: string } | undefined, isOutboundGroup: boolean) => {
    if (!scanType) {
        return 'Please select a Scan Type';
    } else if (!isOutboundGroup) {
        return 'Destination only available for Outbound Scan Types';
    }
    return '';
};

export default ScanCreateEditModal;
